import { Application, Container, Graphics, Sprite, type Texture } from 'pixi.js';
import { Assets } from 'pixi.js';
import type { BrandWatermark, CurrencyFormat, GameMode, Grid, LayoutProfile, LineWin, MathConfig, Position, Presenter, ResolvedTheme, SpinBook, SpinPlan, Strings, WinContext, PresenterLayout } from '../types';
import type { AudioManager, SoundHandle } from '../audio/audio';
import { alphaOf, toPixiColor } from '../util/color';
import { LineLayer } from './lines';
import { LogoLayer } from './logo';
import { MascotLayer } from './mascot';
import { pickMascotCodec } from './mascot-codec';
import { DomBackdrop, DomMascot } from './mascot-dom';
import { OutcomeCard } from './outcome';
import { Overlays } from './overlays';
import { ReelSet } from './reels';
import { RollupText } from './rollup';
import { SymbolFx } from './symbolfx';
import { TextureStore } from './textures';
import { Timeline } from './tween';

export interface PresenterOptions {
  container: HTMLElement;
  theme: ResolvedTheme;
  math: MathConfig;
  audio: AudioManager;
  strings: Strings;
  currency: CurrencyFormat;
  onProgress?: (p: number) => void;
  /** Called after every layout pass with the design→CSS mapping (used by DOM layers such as the mascot). */
  onLayout?: (layout: PresenterLayout) => void;
}

export interface PixiPresenter extends Presenter {
  setTheme(theme: ResolvedTheme): Promise<void>;
  setStrings(strings: Strings): void;
  setFreeSpinsCounter(remaining: number | null, totalWin: number): void;
  /** Brand watermark drawn over the scene in screen space (null removes it). */
  setWatermark(spec: BrandWatermark | null): Promise<void>;
  resize(): void;
  readonly app: Application;
  /** Last layout pass (null before init). */
  readonly layoutInfo: PresenterLayout | null;
}

export async function createPresenter(o: PresenterOptions): Promise<PixiPresenter> {
  const p = new PixiPresenterImpl(o);
  await p.init();
  return p;
}

class PixiPresenterImpl implements PixiPresenter {
  readonly app = new Application();
  private readonly container: HTMLElement;
  private theme: ResolvedTheme;
  private readonly math: MathConfig;
  private readonly audio: AudioManager;
  private strings: Strings;
  private readonly currency: CurrencyFormat;
  private readonly onProgress?: (p: number) => void;
  private readonly onLayout?: (layout: PresenterLayout) => void;
  layoutInfo: PresenterLayout | null = null;

  private textures = new TextureStore();
  private spinTl!: Timeline;
  private winTl!: Timeline;
  private cycleTl!: Timeline;
  private fxTl!: Timeline;

  private readonly bgLayer = new Container();
  private readonly watermarkLayer = new Container();
  private watermark: Sprite | null = null;
  private watermarkSpec: BrandWatermark | null = null;
  private readonly root = new Container();
  private bgBase: Sprite | Graphics | null = null;
  private bgFree: Sprite | Graphics | null = null;
  private logo: LogoLayer | null = null;
  private mascot: MascotLayer | DomMascot | null = null;
  /** WebKit only: theme background as a DOM layer under the DOM mascot (the canvas is transparent). */
  private backdrop: DomBackdrop | null = null;
  private reels!: ReelSet;
  private fx!: SymbolFx;
  private lines!: LineLayer;
  private rollup!: RollupText;
  private overlays!: Overlays;
  private outcome!: OutcomeCard;
  private readonly reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  private profile!: LayoutProfile;
  private grid: Grid = [];
  private mode: GameMode = 'base';
  private resizeObserver: ResizeObserver | null = null;
  private spinLoop: SoundHandle | null = null;
  private anticipationSound: SoundHandle | null = null;
  private music: SoundHandle | null = null;
  private paused = false;
  private destroyed = false;
  private stopCycle: (() => void) | null = null;
  private stopSymbolAnim: (() => void) | null = null;
  private freeSpinsRemaining: number | null = null;
  private freeSpinsTotalWin = 0;

  constructor(o: PresenterOptions) {
    this.container = o.container;
    this.theme = o.theme;
    this.math = o.math;
    this.audio = o.audio;
    this.strings = o.strings;
    this.currency = o.currency;
    this.onProgress = o.onProgress;
    this.onLayout = o.onLayout;
  }

  // ------------------------------------------------------------------ setup

  async init(): Promise<void> {
    const w = Math.max(1, this.container.clientWidth);
    const h = Math.max(1, this.container.clientHeight);
    await this.app.init({
      width: w,
      height: h,
      backgroundAlpha: 0,
      antialias: true,
      resolution: Math.min(globalThis.devicePixelRatio || 1, 2),
      autoDensity: true,
      preference: 'webgl',
    });
    const canvas = this.app.canvas;
    Object.assign(canvas.style, { position: 'absolute', inset: '0', width: '100%', height: '100%', touchAction: 'none', display: 'block' });
    this.container.insertBefore(canvas, this.container.firstChild);

    this.spinTl = new Timeline(this.app.ticker);
    this.winTl = new Timeline(this.app.ticker);
    this.cycleTl = new Timeline(this.app.ticker);
    this.fxTl = new Timeline(this.app.ticker);

    await this.textures.load(this.theme, this.math, this.onProgress);
    if (this.destroyed) return;
    void this.textures.loadWinSheets(this.theme, this.math);

    this.app.stage.addChild(this.bgLayer, this.watermarkLayer, this.root);
    this.buildScene();
    this.resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => this.resize()) : null;
    this.resizeObserver?.observe(this.container);
    this.container.addEventListener('slot:hudresize', this.onHudResize);
    this.resize();
  }

  private readonly onHudResize = () => this.resize();

  private buildScene(): void {
    this.root.removeChildren();
    this.bgLayer.removeChildren();
    this.bgBase = this.makeBackground(this.theme.assets.background?.base, this.theme.palette.background);
    this.bgFree = this.makeBackground(this.theme.assets.background?.freeSpins, this.theme.palette.backgroundFreeSpins);
    this.bgFree.alpha = this.mode === 'free' ? 1 : 0;
    this.bgLayer.addChild(this.bgBase, this.bgFree);

    this.logo = new LogoLayer({ theme: this.theme, textures: this.textures });

    this.reels = new ReelSet({ math: this.math, theme: this.theme, textures: this.textures, timeline: this.spinTl });
    this.fx = new SymbolFx({ theme: this.theme, math: this.math, timeline: this.cycleTl, textures: this.textures });
    this.lines = new LineLayer({ theme: this.theme, math: this.math, timeline: this.cycleTl });
    this.rollup = new RollupText({ theme: this.theme, currency: this.currency, audio: this.audio });
    this.overlays = new Overlays({ theme: this.theme, strings: this.strings, currency: this.currency, textures: this.textures, audio: this.audio });
    this.outcome = new OutcomeCard({ theme: this.theme, strings: this.strings, currency: this.currency, reducedMotion: this.reducedMotion, textures: this.textures });
    this.mascot?.destroy();
    this.backdrop?.destroy();
    this.backdrop = null;
    const m = this.theme.assets.mascot;
    // WebKit plays HEVC-alpha only as a real <video>: DOM mascot under the transparent canvas, background moved to DOM.
    const domRoute = !!m?.idle && !this.reducedMotion && !!m.hevc?.idle && pickMascotCodec(m) === 'hevc';
    this.bgLayer.visible = !domRoute;
    if (domRoute) {
      this.backdrop = new DomBackdrop({ container: this.container, before: this.app.canvas, theme: this.theme, free: this.mode === 'free' });
      this.mascot = new DomMascot({ container: this.container, before: this.app.canvas, clips: m!.hevc!, poster: m!.poster });
    } else this.mascot = m?.idle ? new MascotLayer({ theme: this.theme, ticker: this.app.ticker, reducedMotion: this.reducedMotion }) : null;
    // Mascot sits under everything in the design space so the reel window can overlap it (portrait).
    if (this.mascot instanceof MascotLayer) this.root.addChild(this.mascot);
    if (this.logo) this.root.addChild(this.logo);
    this.root.addChild(this.reels, this.fx.layer, this.lines, this.rollup, this.outcome, this.overlays);
    if (this.grid.length) this.reels.setGrid(this.grid);
    this.fx.setGrid(this.grid);
  }

  private makeBackground(url: string | undefined, color: string): Sprite | Graphics {
    const tex = url ? this.textures.image(url) : null;
    if (tex) return new Sprite(tex);
    return new Graphics().rect(0, 0, 10, 10).fill({ color: toPixiColor(color), alpha: alphaOf(color) });
  }

  /** Space reserved by the DOM HUD (set by the HUD as CSS variables on the container). */
  private hudInsets(): { top: number; bottom: number } {
    const cs = getComputedStyle(this.container);
    const px = (name: string) => {
      const v = parseFloat(cs.getPropertyValue(name));
      return Number.isFinite(v) ? v : null;
    };
    const bottom = px('--slot-hud-bottom') ?? px('--slot-hud-height') ?? 150;
    const top = px('--slot-hud-top') ?? 0;
    return { top, bottom };
  }

  resize(): void {
    if (this.destroyed) return;
    const w = Math.max(1, this.container.clientWidth);
    const h = Math.max(1, this.container.clientHeight);
    this.app.renderer.resize(w, h);
    const portrait = w < h;
    this.profile = portrait ? this.theme.layout.portrait : this.theme.layout.landscape;
    const insets = this.hudInsets();
    const availH = Math.max(100, h - insets.top - insets.bottom);
    const scale = Math.min(w / this.profile.designWidth, availH / this.profile.designHeight);
    this.root.scale.set(scale);
    this.root.position.set((w - this.profile.designWidth * scale) / 2, insets.top + Math.max(0, (availH - this.profile.designHeight * scale) / 2));
    this.placeWatermark(w, h, insets);

    for (const bg of [this.bgBase, this.bgFree]) {
      if (!bg) continue;
      if (bg instanceof Sprite) {
        const tex: Texture = bg.texture;
        const s = Math.max(w / tex.width, h / tex.height);
        bg.scale.set(s);
        bg.position.set((w - tex.width * s) / 2, (h - tex.height * s) / 2);
      } else {
        bg.scale.set(w / 10, h / 10);
      }
    }
    this.logo?.layout(this.profile);

    this.reels.layout(this.profile);
    this.lines.position.copyFrom(this.reels.position);
    this.fx.layer.position.copyFrom(this.reels.position);
    const frame = this.reels.frameSize;
    this.rollup.setFontSize(Math.max(22, this.profile.designWidth * (portrait ? 0.1 : 0.05)));
    this.rollup.position.set(this.profile.designWidth / 2, this.reels.y + frame.h + this.profile.designWidth * (portrait ? 0.12 : 0.04));
    // Win plaque: min(82% of the reel console, 300 design px), 3:2 (ClefDev win-plaque pack).
    this.outcome.layout(Math.min(frame.w * 0.82, 300));
    this.outcome.position.set(this.reels.x + frame.w / 2, this.reels.y + frame.h / 2);
    this.overlays.layout(this.profile);
    this.overlays.setViewport({ x: -this.root.x / scale, y: -this.root.y / scale, w: w / scale, h: h / scale });
    if (this.stopCycle) this.redrawCurrentWin();
    this.layoutInfo = { scale, x: this.root.x, y: this.root.y, portrait, profile: this.profile };
    this.mascot?.layout(this.profile, this.layoutInfo);
    this.onLayout?.(this.layoutInfo);
  }

  // ------------------------------------------------------------------ Presenter

  setGrid(grid: Grid): void {
    this.grid = grid;
    this.reels.setGrid(grid);
    this.fx.setGrid(grid);
  }

  async spin(plan: SpinPlan): Promise<void> {
    if (this.destroyed) return;
    this.grid = plan.book.grid;
    this.fx.setGrid(plan.book.grid);
    this.audio.play('spinStart');
    this.mascot?.idle();
    this.spinLoop?.stop();
    this.spinLoop = this.audio.play('spinLoop');
    const ant = plan.anticipationFromReel;
    const scatter = this.math.scatter?.symbol ?? null;
    const wilds = new Set(this.math.symbols.filter((s) => s.wild).map((s) => s.id));
    const onReelStop = (reel: number) => {
      this.audio.play('reelStop');
      const column = plan.book.grid[reel];
      let sc = false;
      let wd = false;
      column.forEach((id, row) => {
        if (id === scatter) {
          this.fx.land(this.reels, [reel, row], 'scatter');
          sc = true;
        } else if (wilds.has(id)) {
          this.fx.land(this.reels, [reel, row], 'wild');
          wd = true;
        }
      });
      if (sc) this.audio.play('scatterLand');
      else if (wd) this.audio.play('wildLand');
      if (ant != null && reel === ant - 1) {
        this.anticipationSound?.stop();
        this.anticipationSound = this.audio.play('anticipation');
      }
      if (reel === this.math.reels - 1) {
        this.anticipationSound?.stop();
        this.anticipationSound = null;
      }
      plan.onReelStop?.(reel);
    };
    await this.reels.spin(plan, plan.quick || this.reducedMotion, onReelStop);
    // t0: the last reel stopped — the mascot reacts to the round result once.
    if (!this.destroyed) this.mascot?.react(plan.book.totalWin > 0 ? 'win' : 'loss');
    this.spinLoop?.stop();
    this.spinLoop = null;
    this.anticipationSound?.stop();
    this.anticipationSound = null;
  }

  quickStop(): void {
    this.reels.quickStop();
    this.anticipationSound?.stop();
    this.anticipationSound = null;
  }

  private winPositions(book: SpinBook): Position[] {
    const seen = new Set<string>();
    const out: Position[] = [];
    const add = (p: Position) => {
      const k = `${p[0]}:${p[1]}`;
      if (!seen.has(k)) {
        seen.add(k);
        out.push(p);
      }
    };
    for (const w of book.lineWins) w.positions.forEach(add);
    book.scatterWin?.positions.forEach(add);
    return out;
  }

  private currentBook: SpinBook | null = null;
  private currentCtx: WinContext | null = null;
  private cycleIndex = -1;

  /** Schedule the outcome card on the cycle timeline (cancelled by clearWin). */
  private scheduleOutcome(amount: number, lines: number): void {
    const fx = this.theme.animations.effects.outcomeCard;
    if (!fx.enabled) return;
    const delay = this.theme.animations.timing.win.outcomeDelayMs;
    void this.cycleTl.wait(delay).then(() => {
      if (this.destroyed || this.currentBook === null) return;
      this.rollup.set(null);
      void this.outcome.show(amount, lines, this.cycleTl);
    });
  }

  async presentWin(book: SpinBook, ctx: WinContext): Promise<void> {
    if (this.destroyed) return;
    if (book.totalWin <= 0) {
      this.currentBook = book;
      return;
    }
    const T = this.theme.animations.timing.win;
    this.currentBook = book;
    this.currentCtx = ctx;
    this.cycleIndex = -1;
    await this.winTl.wait(T.revealDelayMs);
    if (this.destroyed || this.currentBook !== book) return;
    this.redrawCurrentWin();
    const p = this.theme.palette;
    const color = toPixiColor(ctx.tier === 'epic' ? p.winEpic : ctx.tier === 'mega' ? p.winMega : ctx.tier === 'big' ? p.winBig : p.winNormal);
    if (!ctx.celebrate) {
      this.rollup.set(book.totalWin);
      return;
    }
    this.scheduleOutcome(book.totalWin, book.lineWins.length + (book.scatterWin ? 1 : 0));
    if (ctx.tier === 'big' || ctx.tier === 'mega' || ctx.tier === 'epic') {
      await this.overlays.bigWin(ctx.tier, book.totalWin, this.winTl);
      if (this.destroyed || this.currentBook !== book) return;
      this.rollup.set(book.totalWin);
    } else {
      this.audio.play(book.totalWin >= ctx.bet * 5 ? 'winMedium' : 'winSmall');
      await this.rollup.rollTo(book.totalWin, T.rollupMs, this.winTl, color);
      if (this.destroyed || this.currentBook !== book) return;
    }
    // Lines, dimming and the symbol animation keep running until the next spin clears them (clearWin);
    // with several lines they cycle one by one after allLinesMs.
    this.startLineCycle(book);
  }

  /**
   * Full redraw of the current win (reveal, resize): dim everything outside the winning cells and start the symbol
   * animation on ALL winning symbols at once. The clips then run uninterrupted for the whole presentation;
   * only the lines cycle (see redrawLines), so nothing restarts or flickers when the cycle moves on.
   */
  private redrawCurrentWin(): void {
    const book = this.currentBook;
    const ctx = this.currentCtx;
    if (!book || !ctx) return;
    const positions = this.winPositions(book);
    this.fx.dim(this.reels, positions);
    this.redrawLines();
    this.stopSymbolAnim?.();
    this.stopSymbolAnim = ctx.celebrate ? this.fx.animate(this.reels, positions, this.theme.animations.effects.symbolWin, this.theme.animations.timing.win.symbolLoopMs) : null;
  }

  /** Lines only: all of them, or the one the cycle is on. */
  private redrawLines(): void {
    const book = this.currentBook;
    if (!book) return;
    const cell = (r: number, c: number) => this.reels.cellPosition(r, c);
    const size = this.reels.symbolSize;
    const win: LineWin | null = this.cycleIndex >= 0 ? book.lineWins[this.cycleIndex] : null;
    if (win) this.lines.showLine(win, cell, size);
    else this.lines.showLines(book.lineWins, cell, size);
  }

  private startLineCycle(book: SpinBook): void {
    this.stopCycle?.();
    this.stopCycle = null;
    if (book.lineWins.length < 2 && !(book.lineWins.length === 1 && book.scatterWin)) return;
    const T = this.theme.animations.timing.win;
    let last = 0;
    let started = false;
    const stopWait = this.cycleTl.loop({
      period: T.allLinesMs,
      update: (p) => {
        if (!started && p < last) {
          started = true;
          stopWait();
          this.cycleIndex = 0;
          this.redrawLines();
          let lastP = 0;
          const stopLoop = this.cycleTl.loop({
            period: T.lineCycleMs,
            update: (q) => {
              if (q < lastP) {
                this.cycleIndex = (this.cycleIndex + 1) % (book.lineWins.length + (book.scatterWin ? 1 : 0));
                if (this.cycleIndex >= book.lineWins.length) this.cycleIndex = -1; // show all (incl. scatter) once per cycle
                this.redrawLines();
              }
              lastP = q;
            },
          });
          this.stopCycle = stopLoop;
        }
        last = p;
      },
    });
    this.stopCycle = stopWait;
  }

  skip(): void {
    this.winTl.skip();
  }

  clearWin(): void {
    this.winTl.cancel();
    this.stopCycle?.();
    this.stopCycle = null;
    this.stopSymbolAnim?.();
    this.stopSymbolAnim = null;
    this.cycleTl.cancel();
    this.currentBook = null;
    this.currentCtx = null;
    this.cycleIndex = -1;
    this.fx.clear();
    this.lines.clear();
    this.rollup.set(null);
    this.outcome.hide();
    this.overlays.reset();
    this.audio.stop('rollup');
  }

  async freeSpinsIntro(spins: number): Promise<void> {
    if (this.destroyed) return;
    this.freeSpinsRemaining = spins;
    this.freeSpinsTotalWin = 0;
    await this.overlays.freeSpinsIntro(spins, this.winTl);
    this.overlays.freeSpinsCounter(this.freeSpinsRemaining, this.freeSpinsTotalWin);
  }

  async freeSpinsRetrigger(added: number, remaining: number): Promise<void> {
    if (this.destroyed) return;
    this.freeSpinsRemaining = remaining;
    this.overlays.freeSpinsCounter(remaining, this.freeSpinsTotalWin);
    await this.overlays.freeSpinsRetrigger(added, remaining, this.winTl);
  }

  async freeSpinsOutro(totalWin: number): Promise<void> {
    if (this.destroyed) return;
    this.clearWin();
    this.overlays.freeSpinsCounter(null, 0);
    this.freeSpinsRemaining = null;
    await this.overlays.freeSpinsOutro(totalWin, this.winTl);
  }

  setFreeSpinsCounter(remaining: number | null, totalWin: number): void {
    this.freeSpinsRemaining = remaining;
    this.freeSpinsTotalWin = totalWin;
    if (this.mode === 'free') this.overlays.freeSpinsCounter(remaining, totalWin);
  }

  setMode(mode: GameMode): void {
    const changed = mode !== this.mode;
    this.mode = mode;
    if (this.bgFree) {
      const from = this.bgFree.alpha;
      const to = mode === 'free' ? 1 : 0;
      if (changed) void this.fxTl.tween({ duration: 600, update: (t) => (this.bgFree!.alpha = from + (to - from) * t) });
      else this.bgFree.alpha = to;
    }
    this.backdrop?.setFree(mode === 'free', changed);
    if (mode === 'base') this.overlays.freeSpinsCounter(null, 0);
    this.startMusic();
  }

  private startMusic(): void {
    this.music?.stop();
    this.music = this.paused ? null : this.audio.play(this.mode === 'free' ? 'musicFree' : 'musicBase');
  }

  setPaused(paused: boolean): void {
    if (this.destroyed || this.paused === paused) return;
    this.paused = paused;
    if (paused) {
      this.app.ticker.stop();
      this.audio.stopAll();
      this.spinLoop = null;
      this.anticipationSound = null;
      this.music = null;
    } else {
      this.app.ticker.start();
      this.startMusic();
    }
  }

  // ------------------------------------------------------------------ watermark

  async setWatermark(spec: BrandWatermark | null): Promise<void> {
    this.watermarkSpec = spec;
    if (this.watermark) {
      this.watermark.destroy();
      this.watermark = null;
    }
    if (!spec || this.destroyed) return;
    try {
      const tex = await Assets.load(spec.src);
      if (this.destroyed || this.watermarkSpec !== spec) return;
      this.watermark = new Sprite(tex);
      this.watermark.alpha = spec.alpha;
      this.watermarkLayer.addChild(this.watermark);
      this.resize();
    } catch {
      console.warn(`[simple-slot] watermark failed to load: ${spec.src}`);
    }
  }

  private placeWatermark(w: number, h: number, insets: { top: number; bottom: number }): void {
    const wm = this.watermark;
    const spec = this.watermarkSpec;
    if (!wm || !spec) return;
    const ratio = wm.texture.height / Math.max(1, wm.texture.width);
    wm.width = spec.width;
    wm.height = spec.width * ratio;
    const m = spec.margin;
    const x = spec.position.endsWith('left') ? m : w - spec.width - m;
    const y = spec.position.startsWith('top') ? insets.top + m : h - insets.bottom - wm.height - m;
    wm.position.set(x, y);
  }

  // ------------------------------------------------------------------ theme

  setStrings(strings: Strings): void {
    this.strings = strings;
    this.overlays.setStrings(strings);
    this.outcome.setStrings(strings);
  }

  async setTheme(theme: ResolvedTheme): Promise<void> {
    if (this.destroyed) return;
    this.clearWin();
    const old = this.textures;
    this.textures = new TextureStore();
    await this.textures.load(theme, this.math, this.onProgress);
    if (this.destroyed) return;
    void this.textures.loadWinSheets(theme, this.math);
    this.theme = theme;
    this.audio.setTheme(theme);
    this.mascot?.destroy();
    this.mascot = null;
    for (const child of [...this.root.children]) child.destroy({ children: true });
    for (const child of [...this.bgLayer.children]) child.destroy({ children: true });
    this.buildScene();
    old.destroy(this.textures.urls);
    this.resize();
    this.startMusic();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.resizeObserver?.disconnect();
    this.container.removeEventListener('slot:hudresize', this.onHudResize);
    this.spinLoop?.stop();
    this.anticipationSound?.stop();
    this.music?.stop();
    this.stopCycle?.();
    this.stopSymbolAnim?.();
    for (const tl of [this.spinTl, this.winTl, this.cycleTl, this.fxTl]) tl?.destroy();
    this.mascot?.destroy();
    this.mascot = null;
    this.backdrop?.destroy();
    this.backdrop = null;
    this.textures.destroy();
    this.app.destroy(true, { children: true });
  }
}
